import React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  UnifiedTable,
  UnifiedTableColumn,
  UnifiedTableDensity,
  UnifiedTableVariant,
} from "@/components/ui/unifiedTable/UnifiedTable";

interface DemoParticipantRow {
  id: string;
  participant: string;
  program: string;
  status: "Active" | "Pending" | "Paused";
  assignedClinician: string;
  lastActivity: string;
  notesOpen: number;
}

const demoRows: DemoParticipantRow[] = [
  { id: "200011", participant: "Maya Patel", program: "ZODR", status: "Active", assignedClinician: "Dr. Singh", lastActivity: "2026-02-10", notesOpen: 1 },
  { id: "200045", participant: "Elias Kim", program: "MIST", status: "Pending", assignedClinician: "L. Romero", lastActivity: "2026-02-09", notesOpen: 3 },
  { id: "200056", participant: "Nicole Torres", program: "FIST", status: "Active", assignedClinician: "A. Reza", lastActivity: "2026-02-08", notesOpen: 2 },
  { id: "200072", participant: "Devon Ross", program: "PRG_WMN", status: "Paused", assignedClinician: "S. Nash", lastActivity: "2026-02-05", notesOpen: 0 },
  { id: "200089", participant: "Harper Chen", program: "LEAD", status: "Active", assignedClinician: "T. Alvarez", lastActivity: "2026-02-11", notesOpen: 4 },
  { id: "200095", participant: "Jordan Clark", program: "DSH", status: "Pending", assignedClinician: "M. Ali", lastActivity: "2026-02-07", notesOpen: 2 },
];

const statusClass = (status: DemoParticipantRow["status"]): string => {
  if (status === "Active") {
    return "bg-[var(--semantic-success)]/15 text-[var(--neutral-gray-120)] border-[var(--semantic-success)]/40";
  }

  if (status === "Pending") {
    return "bg-[var(--semantic-warning)]/15 text-[var(--neutral-gray-120)] border-[var(--semantic-warning)]/40";
  }

  return "bg-[var(--neutral-gray-30)] text-[var(--neutral-gray-100)] border-[var(--neutral-gray-50)]";
};

const variantOptions: Array<{ id: UnifiedTableVariant; label: string; helper: string }> = [
  { id: "default", label: "Default", helper: "Balanced spacing and neutral rows" },
  { id: "striped", label: "Striped", helper: "Alternating row backgrounds for scanability" },
  { id: "compact", label: "Compact", helper: "Tighter text density for admin-heavy screens" },
  { id: "bordered", label: "Bordered", helper: "Visible cell boundaries for dense comparisons" },
];

export const UnifiedTableDemoPage = () => {
  const [activeVariant, setActiveVariant] = React.useState<UnifiedTableVariant>("striped");
  const [density, setDensity] = React.useState<UnifiedTableDensity>("compact");
  const [searchEnabled, setSearchEnabled] = React.useState(true);
  const [showLoading, setShowLoading] = React.useState(false);
  const [showEmptyPreview, setShowEmptyPreview] = React.useState(false);

  const demoColumns = React.useMemo<UnifiedTableColumn<DemoParticipantRow>[]>(
    () => [
      {
        key: "participant",
        header: "Participant",
        field: "participant",
        sortable: true,
      },
      {
        key: "program",
        header: "Program",
        field: "program",
        sortable: true,
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        searchValue: (row) => row.status,
        accessor: (row) => (
          <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${statusClass(row.status)}`}>
            {row.status}
          </span>
        ),
      },
      {
        key: "assignedClinician",
        header: "Assigned Clinician",
        field: "assignedClinician",
        sortable: true,
      },
      {
        key: "lastActivity",
        header: "Last Activity",
        field: "lastActivity",
        sortable: true,
      },
      {
        key: "notesOpen",
        header: "Open Notes",
        field: "notesOpen",
        sortable: true,
        align: "right",
      },
      {
        key: "actions",
        header: "Actions",
        align: "right",
        accessor: () => (
          <Button variant="outline" size="sm">
            Open
          </Button>
        ),
      },
    ],
    []
  );

  const activeRows = showEmptyPreview ? [] : demoRows;

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 px-6 py-6">
      <Card>
        <CardHeader>
          <CardTitle>Unified Table Control Playground</CardTitle>
          <CardDescription>
            Use this page to compare table variants before applying a single style system across the app.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <p className="text-sm font-semibold text-[var(--neutral-gray-110)]">Variant</p>
            <div className="flex flex-wrap gap-2">
              {variantOptions.map((option) => (
                <Button
                  key={option.id}
                  type="button"
                  variant={activeVariant === option.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveVariant(option.id)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
            <p className="text-xs text-[var(--neutral-gray-100)]">
              {variantOptions.find((option) => option.id === activeVariant)?.helper}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant={density === "comfortable" ? "default" : "outline"}
              size="sm"
              onClick={() => setDensity("comfortable")}
            >
              Comfortable
            </Button>
            <Button
              type="button"
              variant={density === "compact" ? "default" : "outline"}
              size="sm"
              onClick={() => setDensity("compact")}
            >
              Compact
            </Button>
            <Button
              type="button"
              variant={searchEnabled ? "default" : "outline"}
              size="sm"
              onClick={() => setSearchEnabled((previousValue) => !previousValue)}
            >
              Optional Search: {searchEnabled ? "On" : "Off"}
            </Button>
            <Button
              type="button"
              variant={showLoading ? "default" : "outline"}
              size="sm"
              onClick={() => setShowLoading((previousValue) => !previousValue)}
            >
              Loading Preview: {showLoading ? "On" : "Off"}
            </Button>
            <Button
              type="button"
              variant={showEmptyPreview ? "default" : "outline"}
              size="sm"
              onClick={() => setShowEmptyPreview((previousValue) => !previousValue)}
            >
              Empty-State Preview: {showEmptyPreview ? "On" : "Off"}
            </Button>
          </div>

          <UnifiedTable<DemoParticipantRow>
            columns={demoColumns}
            data={activeRows}
            rowKey={(row) => row.id}
            searchEnabled={searchEnabled}
            searchPlaceholder="Search by participant, program, or clinician"
            loading={showLoading}
            variant={activeVariant}
            density={density}
            emptyTitle="No participants found"
            emptyDescription="Try changing search or filters."
            toolbarRight={
              <div className="text-xs text-[var(--neutral-gray-100)]">
                Variant: <strong className="capitalize text-[var(--neutral-gray-120)]">{activeVariant}</strong>
              </div>
            }
          />
        </CardContent>
      </Card>
    </div>
  );
};
